// src/components/mobile/MobileGroupFrontPage.jsx
//
// Mobile Group front page (Approach B, Mobile Groups tier) — the mauve sibling of the mobile
// locality front page, and the mobile twin of the desktop GroupFrontPage. Puts mobile groups
// on the unified shell: it mounts the SHARED group columns (GroupChatColumn / GroupEventsColumn /
// NewsGrid / SwapColumn) and the SHARED useGroupHome hook — no forked mobile card rendering.
//
// Layout (top → bottom):
//   GroupIdentityCard (persistent: logo · name · …more · status line)
//   reserved ad slot (0px collapsed — structural, expands later without re-layout)
//   content tab bar (Home · News · Chat · Events · Market[greyed]) — mauve file-folder frame
//   active content column (inside the framed body: sides + bottom, top open so the active
//                          tab's white bleeds into the content — replicates the locality frame)
//
// The Home tab (GroupHomePanel) carries the group's identity, membership control, and the
// one-level hierarchy (parent up-link + sub-groups + federation members). The old GroupSheet
// bottom-sheet was removed (Tier 2 addendum) once the Home tab fully subsumed it. The federation
// MEMBERSHIP statement (when this group belongs to a federation, "A member of the X Federation")
// is a sibling of GroupHomePanel here, not inside it — its own flex column in the 'home' case
// below, pinned beneath GroupHomePanel's scrollable region, mirroring GroupHomeColumn's pinned
// footer on desktop. See FederationMembershipStatement.jsx.
//
// The hook runs ONCE here; its return is handed to GroupIdentityCard and GroupHomePanel as `home`,
// so membership/hierarchy is fetched a single time. Frame colour is theme.primary (fallback
// tokens.colors.contextGroups — never a hardcoded mauve).
//
// NOTE (Mobile Groups, Commit 3): built + compile-validated, NOT yet mounted. MainFeed reroutes
// to it in Commit 4 (which also relaxes the header tab-bar gate and retires the old drawer path).

import React, { useState, useEffect } from 'react';
import { Home, Newspaper, MessageCircle, Calendar, Store } from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { tokens } from '../../styles/designTokens';
import { supabaseFetch } from '../../lib/supabase';
import { parseEventDate } from '../../utils/eventDateTime';
import { useGroupHome } from '../../hooks/useGroupHome';

import MobileActionLine from '../core/HeadersAndFooters/MobileActionLine';
import NewsGrid from '../news/NewsGrid';
import GroupChatColumn from '../frontpage/columns/GroupChatColumn';
import GroupEventsColumn from '../frontpage/columns/GroupEventsColumn';
import SwapColumn from '../frontpage/columns/SwapColumn';
import GroupHomePanel from '../groups/GroupHomePanel';
import FederationMembershipStatement from '../groups/FederationMembershipStatement';
import GroupIdentityCard from './GroupIdentityCard';
import GroupClosingBanner from '../groups/GroupClosingBanner';
import JoinRequestModal from '../groups/JoinRequestModal';
import FederationApplyModal from '../groups/FederationApplyModal';
import ShareLinkModal from '../core/ShareLinkModal';
import ConfirmationModal from '../core/ConfirmationModal';

const MobileGroupFrontPage = ({
  group,
  articles = [],
  chats = [],
  events = [],
  onArticleClick,
  onChatClick,
  onEventClick,
  // Chat accordion props
  openChatThreadId = null,
  onChatThreadToggle,
  onChatsRefresh,
  currentUser,
  onGroupSelect,
  onMembershipChange,
  groupClosing = null,   // useGroupClosing state from MainFeed (Roadmap #103); null = not closing
  canWriteChat = false,   // members only may write in a group's chat (RLS "Members write where they can read")
  // Active tab — controlled (activeScreen + onScreenChange) or self-managed if onScreenChange absent
  activeScreen = 'news',
  onScreenChange,
  hideSearch = false      // Phase B1: demo mode — search_articles only sees public, so no search box
}) => {
  const theme = useTheme();
  const { t } = useTranslation('groups');
  const { requireAuth } = useAuth();
  const mauve = theme?.primary || tokens.colors.contextGroups;

  // Single hook instance for the whole page — shared with the card + sheet via `home`.
  const home = useGroupHome(group, currentUser, { onMembershipChange });

  const [internalTab, setInternalTab] = useState(activeScreen);
  const tab = onScreenChange ? activeScreen : internalTab;
  const changeTab = (id) => {
    if (id === 'market') return; // coming-soon, greyed — not selectable
    if (onScreenChange) onScreenChange(id); else setInternalTab(id);
  };

  // Row-3 create action (under the tabs) — creates the ACTIVE tab's content scoped to the group:
  // news/events navigate to the editors with groupId (→ community_id); chat fires the composer
  // event GroupChatColumn's ConcertinaThread honours (group-scoped onCreateTopLevel). Mirrors the
  // locality action line's behaviour. Market is non-selectable, so `tab` is always news/chat/events.
  const handleCreate = () => {
    switch (tab) {
      case 'news':
        requireAuth('gate_body_post', () => {
          window.dispatchEvent(new CustomEvent('ournet:navigate', {
            detail: { view: 'walking-composer', groupId: group?.id, groupName: group?.name }
          }));
        });
        break;
      case 'chat':
        requireAuth('gate_body_create_chat', () => {
          window.dispatchEvent(new CustomEvent('ournet:open-chat-composer'));
        });
        break;
      case 'events':
        requireAuth('gate_body_add_event', () => {
          window.dispatchEvent(new CustomEvent('ournet:navigate', {
            detail: { view: 'new-event', mode: 'add', groupId: group?.id, groupName: group?.name }
          }));
        });
        break;
      default:
        break;
    }
  };

  // Create label — the SAME keys the locality action line uses, so both contexts read identically
  // (news → frontpage:mobile_create_walking, i.e. "Write an article"). No new i18n keys.
  const createLabel = (() => {
    switch (tab) {
      case 'news': return t('frontpage:mobile_create_walking') || 'Write an article';
      case 'chat': return t('frontpage:mobile_create_chat') || 'New chat';
      case 'events': return t('frontpage:mobile_create_event') || 'New event';
      default: return '';
    }
  })();

  // Event details (start_date, venue_name etc.) — mirrors GroupFrontPage / MobileFrontPage.
  const [eventDetails, setEventDetails] = useState({});
  useEffect(() => {
    if (!events || events.length === 0) { setEventDetails({}); return; }
    let cancelled = false;
    (async () => {
      const eventIds = events.map(e => e.id).filter(Boolean);
      if (eventIds.length === 0) return;
      const { data, error } = await supabaseFetch.query('event_details', {
        select: 'article_id,start_date,end_date,display_from,venue_name,venue_address',
        inFilter: { article_id: eventIds }
      });
      if (error) { console.error('Error fetching event_details:', error); return; }
      if (!cancelled && data) {
        const map = {};
        for (const d of data) map[d.article_id] = d;
        setEventDetails(map);
      }
    })();
    return () => { cancelled = true; };
  }, [events]);

  // News — articles of type news (website-embed articles ride the news family; already
  // hoisted to the top by getGroupArticles).
  const newsArticles = (articles || []).filter(a => a.article_type === 'news' || a.article_type === 'website');

  // Chat — originals, newest activity first; drop orphan gravestones (soft-deleted, no live replies).
  const chatData = (chats || [])
    .filter(c => !c.parent_chat_id && !(c.is_deleted_by_author && (c.reply_count || 0) === 0))
    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

  // Events — merge details, within display window, soonest first.
  // Routed through the shared helper (and parseEventDate) so this matches its three
  // siblings exactly — fixes the pre-existing raw-new-Date() timezone drift here.
  const eventData = (events || [])
    .filter(e => e.article_type === 'event')
    .map(e => {
      const d = eventDetails[e.id];
      return {
        ...e,
        start_date: d?.start_date || null,
        end_date: d?.end_date || null,
        display_from: d?.display_from || null,
        venue_name: d?.venue_name || null,
        venue_address: d?.venue_address || null
      };
    })
    // Full roster — the Events column owns the Future/Past split (no feed-window pre-filter here).
    .sort((a, b) => parseEventDate(a.start_date) - parseEventDate(b.start_date));

  const TABS = [
    { id: 'home',   label: t('tab_home') || 'Home',                              icon: Home },
    { id: 'news',   label: t('frontpage:mobile_screen_news') || 'News',          icon: Newspaper },
    { id: 'chat',   label: t('frontpage:mobile_screen_chat') || 'Chat',          icon: MessageCircle },
    { id: 'events', label: t('frontpage:mobile_screen_events') || 'Events',      icon: Calendar },
    { id: 'market', label: t('frontpage:mobile_screen_marketplace') || 'Market', icon: Store, comingSoon: true }
  ];

  // Per-tab border treatment — faithful replication of the locality composite (HeaderButtonsFeed
  // isMobileFP tabStyle), in mauve: active tab carries top + L/R borders with square-dropped
  // bottom so its white bleeds into the framed body below; inactive tabs carry the bottom divider.
  const tabStyle = (active) => ({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    cursor: 'pointer',
    boxSizing: 'border-box',
    fontFamily: tokens.typography.fontFamily,
    background: active ? 'white' : tokens.colors.headerBackground,
    borderTop: active ? `2px solid ${mauve}` : '2px solid transparent',
    borderLeft: active ? `2px solid ${mauve}` : 'none',
    borderRight: active ? `2px solid ${mauve}` : 'none',
    borderBottom: active ? 'none' : `2px solid ${mauve}`,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px'
  });

  const renderContent = () => {
    switch (tab) {
      case 'home':
        // Own flex column, mirroring ModeColumn's body/footer split (GroupHomeColumn's desktop
        // equivalent): GroupHomePanel gets the scrollable region, the federation membership
        // statement (when federated) gets a pinned, guaranteed-visible slot below it. Without
        // this wrapper, GroupHomePanel's own `height:'100%'` would consume the entire framed
        // body (a plain, non-flex `overflow:'hidden'` block one level up) and a bare sibling
        // after it would be pushed out of view — a real rendering failure, not a style choice
        // (caught in Advisor consultation before this was built, not after).
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <GroupHomePanel
                group={home.completeGroup}
                home={home}
                onGroupSelect={onGroupSelect}
              />
            </div>
            {home.federationGroup && (
              <div style={{ flexShrink: 0 }}>
                <FederationMembershipStatement
                  federationGroup={home.federationGroup}
                  onGroupSelect={onGroupSelect}
                />
              </div>
            )}
          </div>
        );
      case 'news':
        return (
          <NewsGrid
            articles={newsArticles}
            onArticleClick={onArticleClick}
            currentContext={{ type: 'group' }}
            isMobile={true}
            selectedGroup={group}
          />
        );
      case 'chat':
        return (
          <div style={{ height: '100%', overflow: 'hidden' }}>
            <GroupChatColumn
              data={chatData}
              group={group}
              openThreadId={openChatThreadId}
              onThreadToggle={onChatThreadToggle}
              onChatsRefresh={onChatsRefresh}
              hideColumnHeader={true}
              canWrite={canWriteChat}
            />
          </div>
        );
      case 'events':
        return (
          <GroupEventsColumn
            data={eventData}
            onClick={onEventClick}
            group={group}
            hideColumnHeader={true}
            canCreate={!groupClosing?.closing}
          />
        );
      case 'market':
        return <SwapColumn data={[]} comingSoon={true} hideColumnHeader={true} />;
      default:
        return null;
    }
  };

  if (!group) return null;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
      fontFamily: tokens.typography.fontFamily
    }}>
      {/* Persistent identity card — above the frame's open top */}
      <GroupIdentityCard
        group={home.completeGroup}
        home={home}
      />

      {/* A closing group's notice (Roadmap #103): in-flow below the identity card, above the tab
          frame (LAYOUT.md § Mobile Group Front Page order otherwise unchanged). */}
      <div style={{ flexShrink: 0 }}>
        <GroupClosingBanner
          closing={!!groupClosing?.closing}
          closesOn={groupClosing?.closesOn}
          variant="group"
          canUndo={!!groupClosing?.canUndo}
          undoing={!!groupClosing?.undoing}
          undoFailed={!!groupClosing?.undoFailed}
          onUndo={groupClosing?.undo}
        />
      </div>

      {/* Reserved ad slot — 0px collapsed; structurally present so it expands without re-layout */}
      <div data-ad-slot="group-mobile" style={{ height: 0, overflow: 'hidden', flexShrink: 0 }} />

      {/* Content tab bar — the mauve file-folder frame begins here */}
      <div style={{ height: '44px', display: 'flex', alignItems: 'stretch', flexShrink: 0 }}>
        {TABS.map(tabDef => {
          const active = tabDef.id === tab;
          const Icon = tabDef.icon;
          const color = tabDef.comingSoon
            ? tokens.colors.disabledText
            : (active ? mauve : tokens.colors.textSecondary);
          return (
            <button
              key={tabDef.id}
              onClick={() => changeTab(tabDef.id)}
              disabled={tabDef.comingSoon}
              style={{ ...tabStyle(active), cursor: tabDef.comingSoon ? 'default' : 'pointer' }}
            >
              <Icon size={16} strokeWidth={2} color={color} />
              <span style={{
                fontSize: '9px',
                fontWeight: active ? 700 : 500,
                color,
                letterSpacing: '0.2px',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {tabDef.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Row-3 action line — UNDER the tabs (matches locality: Row 1 → tabs → action line → body).
          The shared MobileActionLine; its 2px mauve L/R border connects the tab frame into the
          content frame below. The active tab's white bleeds down through this line into the body.
          Suppressed on the Home/Info tab — it has no create action and no per-item search; the
          content body's own L/R+bottom borders keep the file-folder frame continuous. */}
      {tab !== 'home' && (
        <MobileActionLine
          contextColor={mauve}
          createLabel={createLabel}
          onCreate={(tab === 'chat' && !canWriteChat) || (tab !== 'chat' && groupClosing?.closing) ? undefined : handleCreate}
          topBorder={false}
          searchPlaceholder={t('ui:search_articles') || 'Search articles...'}
          onSearch={hideSearch ? undefined : (term) => window.dispatchEvent(new CustomEvent('ournet:search-articles', { detail: { searchTerm: term } }))}
        />
      )}

      {/* Framed content body — sides + bottom; top open so the action line's white continues in */}
      <div style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        borderLeft: `2px solid ${mauve}`,
        borderRight: `2px solid ${mauve}`,
        borderBottom: `2px solid ${mauve}`
      }}>
        {renderContent()}
      </div>

      {/* Membership modals — rendered ONCE here (the single owner of the `home` instance), driven
          purely by the shared hook flags. The Home tab (GroupHomePanel) triggers these flags via
          home handlers; centralising the modals here means they can never double-mount. */}
      {home.showJoinModal && (
        <JoinRequestModal
          isOpen={home.showJoinModal}
          onClose={() => home.setShowJoinModal(false)}
          onSubmit={home.handleJoinGroup}
          groupName={group.name}
          loading={home.joinLoading}
        />
      )}
      <ShareLinkModal
        visible={home.showShareModal}
        targetType="community"
        targetId={group.id}
        targetName={group.name}
        currentUser={currentUser}
        onClose={() => home.setShowShareModal(false)}
      />
      <ConfirmationModal
        visible={home.showLeaveConfirmation}
        title={`${t('ui:leave') || 'Leave'}?`}
        message={`${group.name}`}
        onOk={home.confirmLeaveGroup}
        onCancel={() => home.setShowLeaveConfirmation(false)}
        okLabel={t('ui:leave') || 'Leave'}
        cancelLabel={t('common:cancel') || 'Cancel'}
      />
      {home.showFederationApplyModal && (
        <FederationApplyModal
          isOpen={home.showFederationApplyModal}
          onClose={() => home.setShowFederationApplyModal(false)}
          onSubmit={home.submitFederationApplication}
          federationGroupId={group.id}
          federationName={group.name}
          applicantGroups={home.federationApplyEligibility?.applicableGroups || []}
          loading={home.federationApplyLoading}
        />
      )}
    </div>
  );
};

export default MobileGroupFrontPage;
